import { useLocation, useNavigate } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { Box, Button, Typography, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import UserSignupForm from "../components/UserSignupForm";
import MechanicSignupForm from "../components/MechanicSignupForm";
import Header from "../components/header";
import Footer from "../components/footer";
import signupPagePhoto from "../assets/signupPagePhoto.png";
import getApiBase from "../components/getApiBase";

const Signup = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [role, setRole] = useState("User");
  const [inviteToken, setInviteToken] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [joiningShopId, setJoiningShopId] = useState("");
  const [redirectMessage, setRedirectMessage] = useState("");
  const [formStarted, setFormStarted] = useState(false);
  const [roleFromUrl, setRoleFromUrl] = useState(false); // Track if role came from URL

  useEffect(() => {
    // Get all query params in one pass
    const params = new URLSearchParams(location.search);
    const urlRole = params.get("role");
    const urlInviteToken = params.get("invite");
    const urlInviteEmail = params.get("email");
    const urlShopId = params.get("shopId");
    const msg = localStorage.getItem("authRedirectMessage");

    console.log('🔍 Signup.js - URL params:', { urlRole, urlInviteToken, urlInviteEmail, urlShopId });

    if (msg) {
      setRedirectMessage(msg);
      localStorage.removeItem("authRedirectMessage");
    }

    if (urlRole) {
      console.log('✅ Setting role to:', urlRole);
      setRole(urlRole);
      setRoleFromUrl(true); // Role came from URL - hide toggle
    }
    if (urlInviteToken) {
      console.log('✅ Setting inviteToken to:', urlInviteToken);
      setInviteToken(urlInviteToken);
    }
    if (urlInviteEmail) {
      console.log('✅ Setting inviteEmail to:', urlInviteEmail);
      setInviteEmail(urlInviteEmail);
    }
    if (urlShopId) {
      console.log('✅ Setting joiningShopId to:', urlShopId);
      setJoiningShopId(urlShopId);
    }

    window.scrollTo(0, 0);
  }, [location.search]);

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [welcomeName, setWelcomeName] = useState("");
  const [welcomeRole, setWelcomeRole] = useState("");
  const [emailVerificationRequired, setEmailVerificationRequired] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState("");
  const [logoutLoading, setLogoutLoading] = useState(false);

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMsg(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const handlePostSignupLogout = async () => {
  if (logoutLoading) return;

  setLogoutLoading(true);

  try {
    await fetch(`${getApiBase()}/api/auth/logout`, {
      method: "POST",
      credentials: "include",
    });
  } catch (_) {
  } finally {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setWelcomeOpen(false);
    navigate("/login");
    setLogoutLoading(false);
  }
};

  return (
    <>
      <Header />
      <Box
        sx={{
          backgroundImage: `url(${signupPagePhoto})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          minHeight: "vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          px: 2,
          py: 6,
        }}
      >
        <Box
          sx={{
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            borderRadius: 6,
            p: 4,
            boxShadow: "0px 10px 25px rgba(0, 0, 0, 0.15)",
            width: "100%",
            maxWidth: 550,
          }}
        >
          <Typography
            variant="h4"
            align="center"
            sx={{
              fontSize: { xs: "1.8rem", sm: "2.2rem" },
              fontWeight: 800,
              color: "#e63926",
              mb: 4,
              fontFamily: "inherit",
            }}
          >
            Create Your Account
          </Typography>

          {!formStarted && !roleFromUrl && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mb: 3,
                gap: 2,
                bgcolor: "#e3e9f1",
                borderRadius: 10,
                p: 1,
                width: "fit-content",
                mx: "auto",
              }}
            >
              <Box
                component="button"
                onClick={() => setRole("User")}
                style={{
                  border: "none",
                  background: role === "User" ? "#e63926" : "transparent",
                  color: role === "User" ? "#fff" : "#000",
                  borderRadius: 8,
                  padding: "6px 20px",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  fontSize: "1em"
                }}
              >
                Car Owner
              </Box>
              <Box
                component="button"
                onClick={() => setRole("Mechanic")}
                style={{
                  border: "none",
                  background: role === "Mechanic" ? "#1976d2" : "transparent",
                  color: role === "Mechanic" ? "#fff" : "#000",
                  borderRadius: 8,
                  padding: "6px 20px",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: "inherit",fontSize: "1em"
                }}
              >
                Mechanic
              </Box>
            </Box>
          )}

          {role === "User" ? (
            <UserSignupForm
              onSuccess={(data) => {
                // Check if email verification is required
                if (data?.requiresEmailVerification) {
                  setEmailVerificationRequired(true);
                  setVerificationEmail(data.email || "");
                  setWelcomeName(""); // No name for email verification message
                  setWelcomeRole("User");
                } else {
                  setEmailVerificationRequired(false);
                  setWelcomeName(data?.first_name || "");
                  setWelcomeRole("User");
                }
                setWelcomeOpen(true);
              }}
              onError={showSnackbar}
              onFormStart={() => setFormStarted(true)}
              onFormBackToStart={() => setFormStarted(false)}
            />
          ) : (
            <MechanicSignupForm
              onSuccess={(data) => {
                // Check if email verification is required
                if (data?.requiresEmailVerification) {
                  setEmailVerificationRequired(true);
                  setVerificationEmail(data.email || "");
                  setWelcomeName(""); // No name for email verification message
                  setWelcomeRole("Mechanic");
                } else {
                  setEmailVerificationRequired(false);
                  setWelcomeName(data?.first_name || "");
                  setWelcomeRole("Mechanic");
                }
                setWelcomeOpen(true);
              }}
              onError={showSnackbar}
              onFormStart={() => setFormStarted(true)}
              onFormBackToStart={() => setFormStarted(false)}
              inviteToken={inviteToken}
              inviteEmail={inviteEmail}
              joiningShopId={joiningShopId}
            />
          )}

          <Dialog open={welcomeOpen} onClose={handlePostSignupLogout} aria-labelledby="welcome-title">
            <DialogTitle id="welcome-title">
              {emailVerificationRequired ? "📧 Verify Your Email" : `Welcome${welcomeName ? `, ${welcomeName}` : ""}!`}
            </DialogTitle>
            <DialogContent>
              {emailVerificationRequired ? (
                <Box>
                  <Typography variant="body1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                    Account created successfully!
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    We've sent a verification link to:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#1a1a1a", mb: 3 }}>
                    {verificationEmail}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Please check your inbox and click the verification link to activate your account.
                  </Typography>
                  <Box 
                    sx={{ 
                      backgroundColor: "#f5f5f5", 
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      p: 1.5,
                      mb: 2
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500, color: "#666" }}>
                      💡 Can't find it? Check your spam/junk folder
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontSize: "0.875rem", color: "#999" }}>
                    The verification link will expire in 7 days. You must verify your email before you can log in.
                  </Typography>
                </Box>
              ) : (
                "For your security, you'll now be signed out. Please sign back in to continue."
              )}
            </DialogContent>
            <DialogActions>
              <Button
  variant="contained"
  onClick={handlePostSignupLogout}
  disabled={logoutLoading}
>
  {logoutLoading
    ? "Please wait..."
    : emailVerificationRequired
      ? "Got it!"
      : "OK"}
</Button>
            </DialogActions>
          </Dialog>

          <Snackbar
            open={openSnackbar}
            autoHideDuration={3000}
            onClose={() => setOpenSnackbar(false)}
          >
            <Alert
              onClose={() => setOpenSnackbar(false)}
              severity={snackbarSeverity}
              sx={{ width: "100%" }}
            >
              {snackbarMsg}
            </Alert>
          </Snackbar>
        </Box>
      </Box>
      <Footer />
    </>
  );
};

export default Signup;
